import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth } from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, updateDoc, query, where, limit, serverTimestamp, DocumentData, addDoc, orderBy, Timestamp, deleteDoc } from 'firebase/firestore';
import { MeiliSearch } from 'meilisearch';
import Toast from './Toast';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';
import ConfirmationModal from './modals/ConfirmationModalActions';
import { useAuth } from '../AuthContext';
import getCurrentScreen from "../Helper/GetScreen";


import { setPipelineRefresh } from '../redux/pipelineRefresh';
import { useAppSelector, useAppDispatch } from '../redux/store';

interface ProgramType {
    id: string;
    programType: string;
}
interface Country {
    id: string;
    name: string;
}

interface University {
    id: string;
    name: string;
    uniCode: string;
}
interface RelationshipManager {
    name?: string;
    email?: string;
    phone?: string;
    linkedin?: string;
}

interface Remark {
    displayName: any;
    id: string;
    remark: string;
    userId: string;
    createdAt: Timestamp;
}

interface StudentPreferencesProps {
    write: boolean;
}

const StudentPreferences = ({ write }: StudentPreferencesProps) => {

    const dispatch = useAppDispatch();

    const { isAdmin, isManager, isStaff } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
    const pipelineRefresh = useAppSelector(state => state.pipelineRefresh.refresh);

    const { currentSubAgent } = useSubAgentAuth();

    const [activeSection, setActiveSection] = useState<string>("Student Preferences");
    const [isProgramDropdownOpen, setIsProgramDropdownOpen] = useState(false);
    const [isDestinationDropdownOpen, setIsDestinationDropdownOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [otherUniversity, setOtherUniversity] = useState('');
    const [studentName, setStudentName] = useState("Loading...");
    const [studentId, setStudentId] = useState("");
    const [studentPhone, setStudentPhone] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [searchTerm, setSearchTerm] = useState('');

    // Firestore data states
    const [programTypes, setProgramTypes] = useState<ProgramType[]>([]);
    const [selectedProgramType, setSelectedProgramType] = useState<string>('');
    const [selectedProgramName, setSelectedProgramName] = useState<string>('');
    const [programSearchTerm, setProgramSearchTerm] = useState('');

    const [countries, setCountries] = useState<Country[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [selectedCountryName, setSelectedCountryName] = useState<string>('');
    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [isLoadingCountry, setIsLoadingCountry] = useState(false);

    const [universities, setUniversities] = useState<University[]>([]);
    const [selectedUniversities, setSelectedUniversities] = useState<University[]>([]);
    const [selectedUniversityIds, setSelectedUniversityIds] = useState<string[]>([]);
    const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);

    // Remarks states
    const [remarks, setRemarks] = useState<Remark[]>([]);
    const [latestRemark, setLatestRemark] = useState<Remark | null>(null);
    const [newRemark, setNewRemark] = useState('');
    const [showRemarkHistory, setShowRemarkHistory] = useState(false);
    const [isSavingRemark, setIsSavingRemark] = useState(false);
    const [isLoadingRemarkHistory, setIsLoadingRemarkHistory] = useState(false);

    // Store focus state for each dropdown
    const [focusedDropdowns, setFocusedDropdowns] = useState<Record<string, boolean>>({});

    const { leadId } = useParams();
    const inputRef = useRef<HTMLInputElement>(null);
    const programDropdownRef = useRef<HTMLDivElement>(null);
    const destinationDropdownRef = useRef<HTMLDivElement>(null);
    const universitiesInputRef = useRef<HTMLInputElement>(null);
    const universitiesDropdownRef = useRef<HTMLDivElement>(null);
    const preferredStreamDropdownRef = useRef<HTMLDivElement>(null);
    const stateDropdownRef = useRef<HTMLDivElement>(null);
    const statusDropdownRef = useRef<HTMLDivElement>(null);
    const subStatusDropdownRef = useRef<HTMLDivElement>(null);
    const stageDropdownRef = useRef<HTMLDivElement>(null);
    const subStageDropdownRef = useRef<HTMLDivElement>(null);
    const leadSourceDropdownRef = useRef<HTMLDivElement>(null);
    const leadSubSourceDropdownRef = useRef<HTMLDivElement>(null);
    const campaignDropdownRef = useRef<HTMLDivElement>(null);
    const languageDropdownRef = useRef<HTMLDivElement>(null);
    const [relationshipManager, setRelationshipManager] = useState<RelationshipManager | undefined>({});
    const [collaborators, setCollaborators] = useState<RelationshipManager[] | DocumentData | undefined>([]);

    const [selectedLeadSource, setSelectedLeadSource] = useState('');
    const [selectedLeadSourceId, setSelectedLeadSourceId] = useState('');
    const [selectedLeadSubSource, setSelectedLeadSubSource] = useState('');
    const [leadSourceSearchTerm, setLeadSourceSearchTerm] = useState('');
    const [leadSubSourceSearchTerm, setLeadSubSourceSearchTerm] = useState('');
    const [selectedPreferredStream, setSelectedPreferredStream] = useState('');
    const [preferredStreamSearchTerm, setPreferredStreamSearchTerm] = useState('');
    const [isPreferredStreamDropdownOpen, setIsPreferredStreamDropdownOpen] = useState(false);
    const [selectedState, setSelectedState] = useState('');
    const [stateSearchTerm, setStateSearchTerm] = useState('');
    const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusSearchTerm, setStatusSearchTerm] = useState('');
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [selectedStage, setSelectedStage] = useState('');
    const [selectedStageId, setSelectedStageId] = useState('');
    const [selectedStatusId, setSelectedStatusId] = useState('');
    const [selectedSubStage, setSelectedSubStage] = useState('');
    const [selectedSubStatus, setSelectedSubStatus] = useState('');
    const [stageSearchTerm, setStageSearchTerm] = useState('');
    const [subStageSearchTerm, setSubStageSearchTerm] = useState('');
    const [subStatusSearchTerm, setSubStatusSearchTerm] = useState('');
    const [isStageDropdownOpen, setIsStageDropdownOpen] = useState(false);
    const [isSubStageDropdownOpen, setIsSubStageDropdownOpen] = useState(false);
    const [isSubStatusDropdownOpen, setIsSubStatusDropdownOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState('');
    const [campaignSearchTerm, setCampaignSearchTerm] = useState('');
    const [isCampaignDropdownOpen, setIsCampaignDropdownOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isLeadSourceDropdownOpen, setIsLeadSourceDropdownOpen] = useState(false);
    const [isLeadSubSourceDropdownOpen, setIsLeadSubSourceDropdownOpen] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [language, setLanguage] = useState('');
    const [languageSearchTerm, setLanguageSearchTerm] = useState('');
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

    // Collection data states
    const [preferredStreams, setPreferredStreams] = useState<{ id: string; name: string }[]>([]);
    const [leadStates, setLeadStates] = useState<{ id: string; state: string }[]>([]);
    const [leadStatuses, setLeadStatuses] = useState<{ id: string; status: string }[]>([]);
    const [leadSubStatuses, setLeadSubStatuses] = useState<{ id: string; subStatus: string, statusId: string }[]>([]);
    const [leadStages, setLeadStages] = useState<{ id: string; stage: string }[]>([]);
    const [leadSubStages, setLeadSubStages] = useState<{ id: string; subStage: string, stageId: string }[]>([]);
    const [leadSources, setLeadSources] = useState<{ id: string; source: string }[]>([]);
    const [leadSubSources, setLeadSubSources] = useState<{ id: string; subSource: string, sourceId: string }[]>([]);
    const [leadCampaigns, setLeadCampaigns] = useState<{ id: string; campaign: string }[]>([]);
    const [languages, setLanguages] = useState<{ id: string; language: string }[]>([]);

    // modal states
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAdditionalActions, setAdditionalActions] = useState(false);

    const navigate = useNavigate();

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const years = Array.from(
        { length: 11 },
        (_, i) => 2020 + i
    );

    useEffect(() => {
        if (selectedLeadSource) {
            const sourceId = leadSources.find(source => source.source === selectedLeadSource)?.id || '';
            setSelectedLeadSourceId(sourceId);
        }
        if (selectedStage) {
            const stageId = leadStages.find(stage => stage.stage === selectedStage)?.id || '';
            setSelectedStageId(stageId);
        }
        if (selectedStatus) {
            const statusId = leadStatuses.find(status => status.status === selectedStatus)?.id || '';
            setSelectedStatusId(statusId);
        }
    }, [selectedLeadSource, selectedStage, selectedStatus]);

    const fetchData = async () => {
        console.log("Fetching data for leadId 1:>>>>>>>>>>>>>>>>>>>>>>>>>>>>>", leadId);

        try {
            setIsLoading(true);
            const currentUser = auth.currentUser;
            if (!currentUser || !leadId) return;

            // Fetch user data
            // const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            let userData = null;

            let companyId: string | undefined;
            // Get user document to get companyId
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
                userData = {
                    companyId: currentSubAgent.companyId,
                }
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
                userData = {
                    companyId: userDoc.data()?.companyId,
                }
            }

            if (!companyId) return;

            const companyDoc = await getDoc(doc(db, 'companies', companyId));
            if (companyDoc.exists()) {
                setCompanyName(companyDoc.data().name || "Unknown Company");
            }

            // Fetch program types
            const programTypesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/programTypes`));
            const programTypesData = programTypesSnapshot.docs
                .map(doc => ({
                    id: doc.id,
                    programType: doc.data().programType
                }))
                .sort((a, b) => a.programType.localeCompare(b.programType)); // Alphabetical sort

            setProgramTypes(programTypesData);

            // Fetch countries
            const countriesSnapshot = await getDocs(collection(db, 'countries'));
            const countriesData = countriesSnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            // Fetch current lead data
            const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
            if (leadDoc.exists()) {
                const data = leadDoc.data();
                setStudentName(data.name || "Unknown Student");
                setStudentPhone(data.number || "");
                setStudentId(leadDoc.id || "");
                // console.log("User Id: ", data.assignedTo);
                const assigneeDoc = await getDoc(doc(db, 'users', data.assignedTo));
                // console.log("User Data: ", assigneeDoc.data());
                setRelationshipManager(assigneeDoc?.data());

                const collaboratorsData = await Promise.all(
                    Array.isArray(data?.collaborators)
                        ? data.collaborators.map(async (collaboratorId: string) => {
                            const collaboratorDoc = await getDoc(doc(db, 'users', collaboratorId));
                            return collaboratorDoc.data();
                        })
                        : [] // Provide an empty array fallback if collaborators is undefined or not an array
                );

                console.log(`Collaborators: `, collaboratorsData);

                if (collaboratorsData.length > 0) {
                    setCollaborators(collaboratorsData);
                }

                // Fetch only the latest remark initially
                fetchLatestRemark(companyId, leadId);

                // Set selected program
                if (data.programTypeId) {
                    setSelectedProgramType(data.programTypeId);
                    const program = programTypesData.find(p => p.id === data.programTypeId);
                    if (program) setSelectedProgramName(program.programType);
                }

                // Set selected country
                if (data.destinationId) {
                    setSelectedCountry(data.destinationId);
                    const country = countriesData.find(c => c.id === data.destinationId);
                    if (country) setSelectedCountryName(country.name);
                }

                // Set intake
                setSelectedMonth(data.intakeMonth || '');
                setSelectedYear(data.intakeYear || new Date().getFullYear());

                // Set universities
                setSelectedUniversityIds(data.universityIds || []);

                // Initialize dropdown values
                const preferredStream = preferredStreams.find(p => p.id === data.preferredStreamId);
                const state = leadStates.find(s => s.id === data.stateId);
                const status = leadStatuses.find(s => s.id === data.statusId);
                const subStatus = leadSubStatuses.find(s => s.id === data.subStatusId);
                const stage = leadStages.find(s => s.id === data.stageId);
                const subStage = leadSubStages.find(s => s.id === data.subStageId);
                const source = leadSources.find(s => s.id === data.sourceId);
                const subSource = leadSubSources.find(s => s.id === data.subSourceId);
                const campaign = leadCampaigns.find(c => c.id === data.campaignId);

                setSelectedPreferredStream(preferredStream?.name || '');
                setSelectedState(state?.state || '');
                setSelectedStatus(status?.status || '');
                setSelectedSubStatus(subStatus?.subStatus || '');
                setSelectedStage(stage?.stage || '');
                setSelectedSubStage(subStage?.subStage || '');
                setSelectedLeadSource(source?.source || '');
                setSelectedLeadSubSource(subSource?.subSource || '');
                setSelectedCampaign(campaign?.campaign || '');
                setPhoneNumber(data.phoneNumber || '');
                setAdminNotes(data.adminNotes || '');
                setLanguage(data.language || '');
            }

            console.log("Fetching data for leadId 2:>>>>>>>>>>>>>>>>>>>>>>>>>>>>>", leadId);

            // Fetch collection data
            const [
                preferredStreamsData,
                statesData,
                statusesData,
                subStatusesData,
                stagesData,
                subStagesData,
                sourcesData,
                subSourcesData,
                campaignData,
                languageData
            ] = await Promise.all([
                getDocs(collection(db, `companies/${companyId}/preferredStreams`)),
                getDocs(collection(db, `companies/${companyId}/leadStates`)),
                getDocs(collection(db, `companies/${companyId}/leadStatuses`)),
                getDocs(collection(db, `companies/${companyId}/leadSubStatuses`)),
                getDocs(collection(db, `companies/${companyId}/leadStages`)),
                getDocs(collection(db, `companies/${companyId}/leadSubStages`)),
                getDocs(collection(db, `companies/${companyId}/leadSources`)),
                getDocs(collection(db, `companies/${companyId}/leadSubSources`)),
                getDocs(collection(db, `companies/${companyId}/leadCampaigns`)),
                getDocs(collection(db, `companies/${companyId}/language`))
            ]);

            const preferredStreamsArray = preferredStreamsData.docs
                .map(doc => ({ id: doc.id, name: doc.data().preferredStream }))
                .sort((a, b) => a.name.localeCompare(b.name));
            const statesArray = statesData.docs
                .map(doc => ({ id: doc.id, state: doc.data().state }))
                .sort((a, b) => a.state.localeCompare(b.state));

            const statusesArray = statusesData.docs
                .map(doc => ({ id: doc.id, status: doc.data().status }))
                .sort((a, b) => a.status.localeCompare(b.status));

            const subStatusesArray = subStatusesData.docs
                .map(doc => ({ id: doc.id, subStatus: doc.data().subStatus, statusId: doc.data().statusId }))
                .sort((a, b) => a.subStatus.localeCompare(b.subStatus));

            const stagesArray = stagesData.docs
                .map(doc => ({ id: doc.id, stage: doc.data().stage }))
                .sort((a, b) => a.stage.localeCompare(b.stage));

            const subStagesArray = subStagesData.docs
                .map(doc => ({ id: doc.id, subStage: doc.data().subStage, stageId: doc.data().stageId }))
                .sort((a, b) => a.subStage.localeCompare(b.subStage));

            const sourcesArray = sourcesData.docs
                .map(doc => ({ id: doc.id, source: doc.data().source }))
                .sort((a, b) => a.source.localeCompare(b.source));

            const subSourcesArray = subSourcesData.docs
                .map(doc => ({ id: doc.id, subSource: doc.data().subSource, sourceId: doc.data().sourceId }))
                .sort((a, b) => a.subSource.localeCompare(b.subSource));

            const campaignsArray = campaignData.docs
                .map(doc => ({ id: doc.id, campaign: doc.data().campaign }))
                .sort((a, b) => a.campaign.localeCompare(b.campaign));

            const languagesArray = languageData.docs
                .map(doc => ({ id: doc.id, language: doc.data().language }))
                .sort((a, b) => a.language.localeCompare(b.language));

            setPreferredStreams(preferredStreamsArray);
            setLeadStates(statesArray);
            setLeadStatuses(statusesArray);
            setLeadSubStatuses(subStatusesArray);
            setLeadStages(stagesArray);
            setLeadSubStages(subStagesArray);
            setLeadSources(sourcesArray);
            setLeadSubSources(subSourcesArray);
            setLeadCampaigns(campaignsArray);
            setLanguages(languagesArray);

            // Initialize dropdown values using the arrays directly
            if (leadDoc.exists()) {
                const data = leadDoc.data();
                const preferredStream = preferredStreamsArray.find(p => p.id === data.preferredStreamId);
                const state = statesArray.find(s => s.id === data.stateId);
                const status = statusesArray.find(s => s.id === data.statusId);
                const subStatus = subStatusesArray.find(s => s.id === data.subStatusId);
                const stage = stagesArray.find(s => s.id === data.stageId);
                const subStage = subStagesArray.find(s => s.id === data.subStageId);
                const source = sourcesArray.find(s => s.id === data.sourceId);
                const subSource = subSourcesArray.find(s => s.id === data.subSourceId);
                const campaign = campaignsArray.find(c => c.id === data.campaignId);
                const language = languagesArray.find(l => l.id === data.languageId);

                setSelectedPreferredStream(preferredStream?.name || '');
                setSelectedState(state?.state || '');
                setSelectedStatus(status?.status || '');
                setSelectedSubStatus(subStatus?.subStatus || '');
                setSelectedStage(stage?.stage || '');
                setSelectedSubStage(subStage?.subStage || '');
                setSelectedLeadSource(source?.source || '');
                setSelectedLeadSubSource(subSource?.subSource || '');
                setSelectedCampaign(campaign?.campaign || '');
                setLanguage(language?.language || '');
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setStudentName("Error loading student");
            setCompanyName("Error loading company");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [leadId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                programDropdownRef.current &&
                !programDropdownRef.current.contains(event.target as Node)
            ) {
                setIsProgramDropdownOpen(false);
            }
            if (
                destinationDropdownRef.current &&
                !destinationDropdownRef.current.contains(event.target as Node)
            ) {
                setIsDestinationDropdownOpen(false);
            }
            if (
                universitiesDropdownRef.current &&
                universitiesInputRef.current &&
                !universitiesDropdownRef.current.contains(event.target as Node) &&
                !universitiesInputRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
            // Handle click outside for the Other Fields dropdowns
            if (
                preferredStreamDropdownRef.current &&
                !preferredStreamDropdownRef.current.contains(event.target as Node)
            ) {
                setIsPreferredStreamDropdownOpen(false);
            }
            if (
                stateDropdownRef.current &&
                !stateDropdownRef.current.contains(event.target as Node)
            ) {
                setIsStateDropdownOpen(false);
            }
            if (
                statusDropdownRef.current &&
                !statusDropdownRef.current.contains(event.target as Node)
            ) {
                setIsStatusDropdownOpen(false);
            }
            if (
                subStatusDropdownRef.current &&
                !subStatusDropdownRef.current.contains(event.target as Node)
            ) {
                setIsSubStatusDropdownOpen(false);
            }
            if (
                stageDropdownRef.current &&
                !stageDropdownRef.current.contains(event.target as Node)
            ) {
                setIsStageDropdownOpen(false);
            }
            if (
                subStageDropdownRef.current &&
                !subStageDropdownRef.current.contains(event.target as Node)
            ) {
                setIsSubStageDropdownOpen(false);
            }
            if (
                leadSourceDropdownRef.current &&
                !leadSourceDropdownRef.current.contains(event.target as Node)
            ) {
                setIsLeadSourceDropdownOpen(false);
            }
            if (
                leadSubSourceDropdownRef.current &&
                !leadSubSourceDropdownRef.current.contains(event.target as Node)
            ) {
                setIsLeadSubSourceDropdownOpen(false);
            }
            if (
                campaignDropdownRef.current &&
                !campaignDropdownRef.current.contains(event.target as Node)
            ) {
                setIsCampaignDropdownOpen(false);
            }
            if (
                languageDropdownRef.current &&
                !languageDropdownRef.current.contains(event.target as Node)
            ) {
                setIsLanguageDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchSelectedUniversities = async () => {
            if (selectedUniversityIds.length === 0) {
                setSelectedUniversities([]);
                return;
            }

            try {
                const universitiesData: University[] = [];
                for (const id of selectedUniversityIds) {
                    const uniDoc = await getDoc(doc(db, 'universities', id));
                    if (uniDoc.exists()) {
                        universitiesData.push({
                            id: uniDoc.id,
                            name: uniDoc.data().name,
                            uniCode: uniDoc.data().uniCode
                        });
                    }
                }
                setSelectedUniversities(universitiesData);
            } catch (error) {
                console.error("Error fetching selected universities:", error);
            }
        };

        fetchSelectedUniversities();
    }, [selectedUniversityIds]);

    const toggleDropdown = (dropdown: string, forceOpen?: boolean) => {
        if (dropdown === "program") {
            const newState = forceOpen === undefined ? !isProgramDropdownOpen : forceOpen;
            setIsProgramDropdownOpen(newState);
            if (newState) {
                setProgramSearchTerm('');
            } else if (!focusedDropdowns['program']) {
                // If dropdown is closing and not focused, clear the search term
                setProgramSearchTerm('');
            }
        } else if (dropdown === "destination") {
            const newState = forceOpen === undefined ? !isDestinationDropdownOpen : forceOpen;
            setIsDestinationDropdownOpen(newState);
        }
    };

    const handleCountrySearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setSelectedCountryName('');
        setCountrySearchTerm(e.target.value);

        // Always open the dropdown when typing
        if (!isDestinationDropdownOpen) {
            toggleDropdown("destination", true);
        }

        if (value.length < 3) {
            setCountries([]);
            return;
        }

        setIsLoadingCountry(true);
        try {
            const countriesRef = collection(db, 'countries');
            const q = query(
                countriesRef,
                where('name', '>=', value),
                where('name', '<=', value + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const countriesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setCountries(countriesData);
        } catch (error) {
            console.error("Error searching countries:", error);
        } finally {
            setIsLoadingCountry(false);
        }
    }

    const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Always open the dropdown when typing
        if (!isDropdownOpen) {
            setIsDropdownOpen(true);
        }

        if (value.length < 3) {
            setUniversities([]);
            return;
        }

        setIsLoadingUniversities(true);
        try {
            // Using Meilisearch instead of Firestore
            const meiliClient = new MeiliSearch({
                host: process.env.REACT_APP_MEILISEARCH_URL || 'https://search.scholarcred.com',
                apiKey: process.env.REACT_APP_MEILISEARCH_API_KEY || '80cdae41311553c99f1835f65ab59832b662cb1a7ac50deb4edd3f520b47bfc1'
            });

            const index = meiliClient.index('universities');
            const searchResults = await index.search(value, {
                limit: 10
            });

            // Format the results to match the existing structure
            const universitiesData = searchResults.hits.map(hit => ({
                id: hit.id as string,
                name: hit.name as string,
                uniCode: '' // Meilisearch data doesn't have uniCode, leave empty
            }));

            setUniversities(universitiesData);
        } catch (error) {
            console.error("Error searching universities:", error);
        } finally {
            setIsLoadingUniversities(false);
        }
    };

    const handleSelectUniversity = (universityId: string) => {
        if (selectedUniversityIds.length < 10 && !selectedUniversityIds.includes(universityId)) {
            setSelectedUniversityIds([...selectedUniversityIds, universityId]);
            setSearchTerm('');
            setUniversities([]);
            setIsDropdownOpen(false);
        }
    };

    const handleRemoveUniversity = (universityId: string) => {
        setSelectedUniversityIds(selectedUniversityIds.filter((id) => id !== universityId));
    };

    const toggleDropdownuni = (forceOpen?: boolean) => {
        const newState = forceOpen === undefined ? !isDropdownOpen : forceOpen;
        setIsDropdownOpen(newState);
    };

    // Helper to get subAgent name by userId
    const getSubAgentName = async (companyId: string, userId: string) => {
        if (!userId) return null;
        try {
            const subAgentDoc = await getDoc(doc(db, `companies/${companyId}/subAgents/${userId}`));
            if (subAgentDoc.exists()) {
                return subAgentDoc.data().name || null;
            }
        } catch (error) {
            console.error('Error fetching subAgent name:', error);
        }
        return null;
    };

    // Fetch only the latest remark from Firestore
    const fetchLatestRemark = async (companyId: string, leadId: string) => {
        try {
            const remarksRef = collection(db, `companies/${companyId}/leads/${leadId}/remarks`);
            const q = query(remarksRef, orderBy('createdAt', 'desc'), limit(1));
            const remarksSnapshot = await getDocs(q);

            if (!remarksSnapshot.empty) {
                const docSnap = remarksSnapshot.docs[0];
                const remarkData = docSnap.data();
                let displayName = "Unknown User";
                if (remarkData.userId) {
                    // Try subAgent name first
                    const subAgentName = await getSubAgentName(companyId, remarkData.userId);
                    if (subAgentName) {
                        displayName = subAgentName;
                    } else {
                        // Fallback to user name
                        const userName = await fetchUser(remarkData.userId);
                        displayName = userName || remarkData.userId;
                    }
                }
                const latestRemarkData = {
                    id: docSnap.id,
                    ...remarkData,
                    displayName,
                    createdAt: remarkData.createdAt
                } as Remark & { displayName?: string };
                setLatestRemark(latestRemarkData);
                setRemarks([latestRemarkData]);
            }
        } catch (error) {
            console.error('Error fetching latest remark:', error);
        }
    };

    // Fetch all historical remarks from Firestore
    const fetchRemarkHistory = async () => {
        try {
            setIsLoadingRemarkHistory(true);
            const currentUser = auth.currentUser;
            if (!currentUser || !leadId) return;

            let companyId: string | undefined;
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }
            if (!companyId) return;

            const remarksRef = collection(db, `companies/${companyId}/leads/${leadId}/remarks`);
            const q = query(remarksRef, orderBy('createdAt', 'desc'));
            const remarksSnapshot = await getDocs(q);

            if (!remarksSnapshot.empty) {
                    const remarksData = await Promise.all(remarksSnapshot.docs.map(async (docSnap) => {
                    const remarkData = docSnap.data();
                    let displayName = "Unknown User";
                    if (remarkData.userId) {
                        const subAgentName = await getSubAgentName(companyId!, remarkData.userId);
                        if (subAgentName) {
                            displayName = subAgentName;
                        } else {
                            const userName = await fetchUser(remarkData.userId);
                            displayName = userName || remarkData.userId;
                        }
                    }
                    return {
                        id: docSnap.id,
                        ...remarkData,
                        displayName,
                        createdAt: remarkData.createdAt
                    } as Remark & { displayName?: string };
                }));
                setRemarks(remarksData);
            }
        } catch (error) {
            console.error('Error fetching remark history:', error);
        } finally {
            setIsLoadingRemarkHistory(false);
        }
    };

    const fetchUser = async (userId: string) => {
        try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                console.log("User Data: ", userDoc.data().name);
                return userDoc.data().name || "Unknown User";
            } else {
                console.error("User not found:", userId);
                return null;
            }
        } catch (error) {
            console.error("Error fetching user:", error);
            return null;
        }
    };

    // Save a new remark to Firestore
    const saveRemark = async (companyId: string, leadId: string) => {
        if (!newRemark.trim()) return;

        try {
            const remarksRef = collection(db, `companies/${companyId}/leads/${leadId}/remarks`);
            await addDoc(remarksRef, {
                remark: newRemark.trim(),
                userId: auth.currentUser?.uid,
                createdAt: serverTimestamp()
            });

            // If history is showing, fetch full history, otherwise just latest remark
            if (showRemarkHistory) {
                await fetchRemarkHistory();
            } else {
                await fetchLatestRemark(companyId, leadId);
            }

            setNewRemark('');
        } catch (error) {
            console.error('Error saving remark:', error);
            throw error; // Propagate error to be handled by the caller
        }
    };

    // save function to handle saving all data
    const confirmSave = () => {
        // Open confirmation modal
        setIsConfirmModalOpen(true);
    }

    const handleSave = async () => {
        try {
            setIsSaving(true);
            const currentUser = auth.currentUser;
            if (!currentUser || !leadId) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }
            if (!companyId) return;

            // Helper function to return null for empty strings
            const getValue = (value: string) => value.trim() ? value : null;

            // Prepare the update data
            const updateData: Record<string, any> = {
                // Existing fields
                programTypeId: selectedProgramType ? selectedProgramType : null,
                destinationId: selectedCountry ? selectedCountry : null,
                intakeMonth: selectedMonth ? selectedMonth : null,
                intakeYear: selectedYear ? selectedYear : null,
                universityIds: selectedUniversityIds.length > 0 ? selectedUniversityIds : null,

                // New mandatory fields
                name: getValue(studentName),
                phoneNumber: getValue(phoneNumber),
                sourceId: selectedLeadSource ? leadSources.find(s => s.source === selectedLeadSource)?.id || null : null,
                subSourceId: selectedLeadSource !== '' && selectedLeadSubSource ? leadSubSources.find(s => s.subSource === selectedLeadSubSource && s.sourceId === selectedLeadSourceId)?.id || null : null,
                campaignId: selectedCampaign ? leadCampaigns.find(c => c.campaign === selectedCampaign)?.id || null : null,

                // New optional fields
                preferredStreamId: selectedPreferredStream ? preferredStreams.find(p => p.name === selectedPreferredStream)?.id || null : null,
                stateId: selectedState ? leadStates.find(s => s.state === selectedState)?.id || null : null,
                statusId: selectedStatus ? leadStatuses.find(s => s.status === selectedStatus)?.id || null : null,
                stageId: selectedStage ? leadStages.find(s => s.stage === selectedStage)?.id || null : null,
                subStageId: selectedStage !== '' && selectedSubStage ? leadSubStages.find(s => s.subStage === selectedSubStage && s.stageId === selectedStageId)?.id || null : null,
                subStatusId: selectedStatus !== '' && selectedSubStatus ? leadSubStatuses.find(s => s.subStatus === selectedSubStatus && s.statusId === selectedStatusId)?.id || null : null,
                adminNotes: getValue(adminNotes),
                languageId: language ? languages.find(l => l.language === language)?.id || null : null,

                // Update the timestamp
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid,
            };

            // Save lead data
            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await updateDoc(leadRef, updateData);

            // Save remark if there's a new one
            if (newRemark.trim()) {
                await saveRemark(companyId, leadId);
            }

            setToastMessage('All data saved successfully!');
            setIsErrorToast(false);
            setShowToast(true);
            // Refresh the pipeline view if needed
            dispatch(setPipelineRefresh(true));
        } catch (error) {
            setToastMessage('Error saving data. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error("Error saving:", error);
        } finally {
            setIsSaving(false);
            setIsConfirmModalOpen(false);
            setAdditionalActions(true);
        }
    };


    const handleDeleteRemark = async (remarkId: string) => {
        const currentUser = auth.currentUser;
        if (!currentUser || !leadId) return;
        let companyId: string | undefined;
        // Get user document to get companyId
        if (currentSubAgent) {
            companyId = currentSubAgent.companyId;
        }
        else {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            companyId = userDoc.data()?.companyId;
        }
        if (!companyId) return;
        try {
            const remarkRef = doc(db, `companies/${companyId}/leads/${leadId}/remarks`, remarkId);
            await deleteDoc(remarkRef);

            // If history is showing, fetch full history, otherwise just latest remark
            if (showRemarkHistory) {
                await fetchRemarkHistory();
            } else {
                await fetchLatestRemark(companyId, leadId);
            }

            setToastMessage('Remark deleted successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error('Error deleting remark:', error);
            setToastMessage('Error deleting remark. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        }
    }




    if (isLoading) {
        return <div>Loading...</div>;
    }

    const renderDropdown = (
        value: string,
        searchTerm: string,
        placeholder: string,
        options: string[],
        isOpen: boolean,
        setFunction: (value: string) => void,
        setSearchTermFunction: (value: string) => void,
        dropdownName: string,
        dropdownRef: React.RefObject<HTMLDivElement>,
        disabled?: boolean
    ) => {
        // Filter options based on search term - show all options when the search term is empty
        const filteredOptions = searchTerm === ''
            ? options
            : options.filter((option) =>
                option.toLowerCase().includes((searchTerm || '').toLowerCase())
            );

        // Check if this dropdown is focused
        const isFocused = focusedDropdowns[dropdownName] || false;

        // Determine what value to show in the input - important to show the actual value when not editing
        let displayValue;
        if (isFocused || isOpen) {
            // When focused or open, show the search term if it exists
            displayValue = searchTerm;
        } else {
            // When not focused or closed, always show the selected value
            displayValue = value;
        }

        return (
            <div className="relative" ref={dropdownRef}>
                <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                    <div className="relative">
                        <input
                            type="text"
                            value={displayValue}
                            placeholder={placeholder}
                            onFocus={() => {
                                // Mark this dropdown as focused
                                setFocusedDropdowns({ ...focusedDropdowns, [dropdownName]: true });

                                if (!isOpen) {
                                    toggleNewDropdown(dropdownName);
                                }

                                // Initialize search field with empty string
                                setSearchTermFunction('');
                            }}
                            onBlur={() => {
                                // Keep focus state if dropdown is still open
                                if (!isOpen) {
                                    const newFocusedState = { ...focusedDropdowns };
                                    delete newFocusedState[dropdownName];
                                    setFocusedDropdowns(newFocusedState);

                                    // If a valid option was not selected and field is empty, restore the original value
                                    if (searchTerm === '' && value !== '') {
                                        setFunction(value);
                                    }
                                }
                            }}
                            onClick={() => {
                                if (!isOpen) {
                                    toggleNewDropdown(dropdownName);
                                }
                            }}
                            onChange={(e) => {
                                setSearchTermFunction(e.target.value);
                                if (!isOpen) toggleNewDropdown(dropdownName);
                            }}
                            className={`w-full py-2 pl-10 pr-10 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}

                            readOnly={!write || disabled}
                            disabled={!write || disabled}
                        />
                        {value && (
                            <button
                                type="button"
                                className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 ${!write || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFunction('');
                                    setSearchTermFunction('');
                                }}
                                disabled={!write || disabled}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => toggleNewDropdown(dropdownName)}
                    disabled={!write || disabled}
                >
                    <img
                        src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                        alt="Dropdown Arrow"
                        className={`w-7 h-7 transition-transform ${isOpen ? (dropdownName === "status" || dropdownName === "stage" || dropdownName === "campaign") ? '' : 'rotate-180' : (dropdownName === "status" || dropdownName === "stage" || dropdownName === "campaign") ? 'rotate-180' : ''}`}
                    />
                </button>
                {isOpen && (
                    <div className={`absolute left-0 right-0 ${(dropdownName === "status" || dropdownName === "stage" || dropdownName === "campaign") ? "bottom-full mb-1" : "mt-1"} bg-white border rounded-3xl shadow-lg z-10 max-h-[300px] overflow-y-auto`}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    key={option}
                                    onClick={() => {
                                        setFunction(option); // Update the actual value
                                        setSearchTermFunction(''); // Clear search term after selection

                                        // Remove this dropdown from focused state
                                        const newFocusedState = { ...focusedDropdowns };
                                        delete newFocusedState[dropdownName];
                                        setFocusedDropdowns(newFocusedState);

                                        toggleNewDropdown(dropdownName); // Close the dropdown
                                    }}
                                    className="w-full py-2 px-4 text-left text-gray-800 hover:bg-gray-100"
                                >
                                    {option}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-2 text-gray-500">No options found</div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const toggleNewDropdown = (dropdownName: string) => {
        const dropdowns = {
            program: setIsProgramDropdownOpen,
            leadSource: setIsLeadSourceDropdownOpen,
            leadSubSource: setIsLeadSubSourceDropdownOpen,
            preferredStream: setIsPreferredStreamDropdownOpen,
            state: setIsStateDropdownOpen,
            status: setIsStatusDropdownOpen,
            subStatus: setIsSubStatusDropdownOpen,
            stage: setIsStageDropdownOpen,
            subStage: setIsSubStageDropdownOpen,
            campaign: setIsCampaignDropdownOpen,
            language: setIsLanguageDropdownOpen
        };

        Object.entries(dropdowns).forEach(([name, setFunction]) => {
            if (name === dropdownName) {
                // If we're closing this dropdown, also remove it from focused state
                setFunction(prev => {
                    const isClosing = prev;
                    if (isClosing) {
                        // Remove this dropdown from focused dropdowns when closing
                        setFocusedDropdowns(current => {
                            const updated = { ...current };
                            delete updated[name];
                            return updated;
                        });
                    }
                    return !prev;
                });
            } else {
                setFunction(false);
            }
        });
    };
    const sectionClass = "mb-8 bg-white rounded-lg p-6 shadow-sm border border-gray-100";
    const sectionHeaderClass = "text-lg font-semibold text-sky-950 mb-4 pb-2 border-b border-gray-200";
    const inputClass = "w-full py-2 px-4 border border-gray-200 rounded-3xl outline-none ring-2 ring-black";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div className="p-8">
            <div className="flex gap-8">
                {/* Left section - Student Preferences */}
                <div className="w-2/3">
                    <div className={sectionClass}>
                        <h2 className={sectionHeaderClass}>Student Preferences</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="relative" ref={programDropdownRef}>
                                <label htmlFor="program" className="block font-montserrat font-normal text-base text-black mb-2">
                                    Preferred Program
                                </label>
                                {renderDropdown(
                                    selectedProgramName,
                                    programSearchTerm,
                                    "Select Program Type",
                                    programTypes.map(program => program.programType),
                                    isProgramDropdownOpen,
                                    (value) => {
                                        setSelectedProgramName(value);
                                        // Find and set the corresponding program type ID
                                        const program = programTypes.find(p => p.programType === value);
                                        if (program) setSelectedProgramType(program.id);
                                    },
                                    setProgramSearchTerm,
                                    "program",
                                    programDropdownRef
                                )}
                            </div>

                            {/* Intake Selection */}
                            <div className="relative">
                                <label htmlFor="intake" className="block font-montserrat font-normal text-base text-black mb-2">
                                    Intake
                                </label>
                                <div className="relative">
                                    <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                        <div className="flex">
                                            <div className="relative w-1/2 bg-white rounded-l-3xl">
                                                <select
                                                    value={selectedMonth}
                                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                                    className={`w-full py-2 pl-10 pr-8 border-r rounded-l-3xl text-gray-800 appearance-none ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                    disabled={!write}
                                                    aria-readonly={!write}
                                                >
                                                    <option value="">Month</option>
                                                    {months.map((month) => (
                                                        <option key={month} value={month}>
                                                            {month}
                                                        </option>
                                                    ))}
                                                </select>
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                    alt="Dropdown Arrow"
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-7 h-7 pointer-events-none"
                                                />
                                            </div>
                                            <div className="relative w-1/2 bg-white rounded-r-3xl">
                                                <select
                                                    value={selectedYear}
                                                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                                                    className={`w-full py-2 pl-10 pr-8 rounded-r-3xl text-gray-800 appearance-none ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                    disabled={!write}
                                                    aria-readonly={!write}
                                                >
                                                    <option value="">Year</option>
                                                    {years.map((year) => (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    ))}
                                                </select>
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                    alt="Dropdown Arrow"
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-7 h-7 pointer-events-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Destination Dropdown */}
                            <div className="relative" ref={destinationDropdownRef}>
                                <label htmlFor="destination" className="block font-montserrat font-normal text-base text-black mb-2">
                                    Destination
                                </label>
                                <div className="relative">
                                    <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                        <input
                                            type="text"
                                            id="destination"
                                            value={countrySearchTerm || selectedCountryName}
                                            placeholder="Select Country"
                                            onChange={handleCountrySearchChange}
                                            onClick={(e) => {
                                                // Don't toggle the dropdown when clicking on the input field
                                                // This prevents accidental closing while typing/searching
                                                e.stopPropagation();
                                                if (!isDestinationDropdownOpen) {
                                                    toggleDropdown("destination", true);
                                                }
                                            }}
                                            className={`w-full py-2 pl-10 pr-4 border rounded-3xl bg-white text-gray-800 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                            readOnly={!write}
                                            disabled={!write}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        onClick={() => toggleDropdown("destination")}
                                        disabled={!write}
                                    >
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                            alt="Dropdown Arrow"
                                            className={`w-7 h-7 transition-transform ${isDestinationDropdownOpen ? 'rotate-180' : ''}`}
                                        />
                                    </button>

                                    {isDestinationDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-20 max-h-[300px] overflow-hidden">
                                            <div className="p-2">
                                                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                                    {isLoadingCountry ? (
                                                        <div className="px-4 py-2 text-gray-500">Loading...</div>
                                                    ) : countries.length > 0 ? (
                                                        countries.map((country) => (
                                                            <button
                                                                key={country.id}
                                                                className="px-4 py-2 text-left hover:bg-gray-100 rounded-xl cursor-pointer text-sm w-full"
                                                                onClick={() => {
                                                                    setSelectedCountry(country.id);
                                                                    setSelectedCountryName(country.name);
                                                                    setCountrySearchTerm('');
                                                                    setIsDestinationDropdownOpen(false);
                                                                }}>
                                                                <div className="flex justify-between items-center text-base">
                                                                    {country.name}
                                                                </div>
                                                            </button>
                                                        ))
                                                    ) : countrySearchTerm.length < 3 ? (
                                                        <div className="px-4 py-2 text-gray-500">Type at least 3 characters to search</div>
                                                    ) : (
                                                        <div className="px-4 py-2 text-gray-500">No countries found</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Universities Selection */}
                            <div className="relative">
                                <label htmlFor="universities" className="block font-montserrat font-normal text-base text-black mb-2">
                                    Preferred Universities <span className="text-sm font-montserrat font-normal text-gray-900">(choose up to 10)</span>
                                </label>
                                <div className="relative">
                                    <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                        <div className="relative">
                                            <input
                                                ref={universitiesInputRef}
                                                type="text"
                                                id="universities"
                                                placeholder="Search universities"
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                                onClick={(e) => {
                                                    // Don't toggle the dropdown when clicking on the input field
                                                    // This prevents accidental closing while typing/searching
                                                    e.stopPropagation();
                                                    if (!isDropdownOpen) {
                                                        toggleDropdownuni(true);
                                                    }
                                                }}
                                                className={`w-full py-2 pl-10 pr-4 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                readOnly={!write}
                                                disabled={!write}
                                            />
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/Nav-Search.svg`}
                                                alt="Search"
                                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                                            />
                                            <button
                                                type="button"
                                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                onClick={(e) => toggleDropdownuni()}
                                                disabled={!write}
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                    alt="Dropdown Arrow"
                                                    className={`w-7 h-7 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Selected Universities Display */}
                                    {selectedUniversities.length > 0 && (
                                        <div className="mt-2">
                                            <div className="flex flex-wrap gap-2">
                                                {selectedUniversities.map((university) => (
                                                    <div
                                                        key={university.id}
                                                        className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                                                    >
                                                        {university.name}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveUniversity(university.id)}
                                                            className={`text-red-500 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                            disabled={!write}
                                                        >
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                                                alt="Remove"
                                                                className="w-4 h-4"
                                                            />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Dropdown */}
                                    {isDropdownOpen && (
                                        <div
                                            ref={universitiesDropdownRef}
                                            className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-20 max-h-[300px] overflow-hidden"
                                        >
                                            <div className="p-2">
                                                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                                    {isLoadingUniversities ? (
                                                        <div className="px-4 py-2 text-gray-500">Loading...</div>
                                                    ) : universities.length > 0 ? (
                                                        universities.map((university) => (
                                                            <button
                                                                key={university.id}
                                                                onClick={() => {
                                                                    if (!selectedUniversityIds.includes(university.id) && selectedUniversityIds.length < 10) {
                                                                        handleSelectUniversity(university.id);
                                                                    }
                                                                }}
                                                                disabled={selectedUniversityIds.length >= 10 && !selectedUniversityIds.includes(university.id)}
                                                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 rounded-xl ${selectedUniversityIds.includes(university.id)
                                                                    ? 'bg-gray-100'
                                                                    : ''
                                                                    } ${selectedUniversityIds.length >= 10 && !selectedUniversityIds.includes(university.id)
                                                                        ? 'opacity-50 cursor-not-allowed'
                                                                        : ''
                                                                    }`}
                                                            >
                                                                <div className="flex justify-between items-center">
                                                                    <span>{university.name}</span>
                                                                    <span className="text-gray-500 text-sm">{university.uniCode}</span>
                                                                </div>
                                                            </button>
                                                        ))
                                                    ) : searchTerm.length < 3 ? (
                                                        <div className="px-4 py-2 text-gray-500">Type at least 3 characters to search</div>
                                                    ) : (
                                                        <div className="px-4 py-2 text-gray-500">No universities found</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 w-1/3">
                    <div className="">
                        <div className={`${sectionClass} h-fit`}>
                            <h2 className={sectionHeaderClass}>Relationship Manager</h2>
                            {relationshipManager ? (
                                <div className="bg-gray-100 p-3 rounded-3xl">
                                    {relationshipManager.name && (
                                        <div className="flex justify-between items-center mb-1">
                                            <h4
                                                className="text-base font-semibold"
                                                style={{
                                                    background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent'
                                                }}
                                            >
                                                {relationshipManager.name}
                                            </h4>
                                            {relationshipManager.linkedin && (
                                                <a
                                                    href={relationshipManager.linkedin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img
                                                        src={`${process.env.PUBLIC_URL}/images/desktop/entypo-social_linkedin-with-circle.svg`}
                                                        className="w-5 h-5 text-gray-600"
                                                        alt="LinkedIn"
                                                    />
                                                </a>
                                            )}
                                        </div>
                                    )}
                                    {relationshipManager.email && (
                                        <p className="text-xs text-gray-500 mb-1">
                                            {relationshipManager.email}
                                        </p>
                                    )}
                                    {relationshipManager.phone && (
                                        <p className="text-sm text-gray-600">
                                            {relationshipManager.phone}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-gray-100 p-3 rounded-3xl">
                                    <p className="text-sm text-gray-500">
                                        No relationship manager assigned
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    {collaborators && collaborators.length > 0 && (
                        <div className="">
                            <div className={`${sectionClass} h-fit`}>
                                <h2 className={sectionHeaderClass}>Collaborators</h2>
                                <div className=''>
                                    {collaborators && collaborators.map((collaborator: RelationshipManager) => (
                                        <div className="bg-gray-100 p-3 rounded-3xl mb-2">
                                            {collaborator.name && (
                                                <div className="flex justify-between items-center mb-1">
                                                    <h4
                                                        className="text-base font-semibold"
                                                        style={{
                                                            background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)',
                                                            WebkitBackgroundClip: 'text',
                                                            WebkitTextFillColor: 'transparent'
                                                        }}
                                                    >
                                                        {collaborator.name}
                                                    </h4>
                                                    {collaborator.linkedin && (
                                                        <a
                                                            href={collaborator.linkedin}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/images/desktop/entypo-social_linkedin-with-circle.svg`}
                                                                className="w-5 h-5 text-gray-600"
                                                                alt="LinkedIn"
                                                            />
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                            {collaborator.email && (
                                                <p className="text-xs text-gray-500 mb-1">
                                                    {collaborator.email}
                                                </p>
                                            )}
                                            {collaborator.phone && (
                                                <p className="text-sm text-gray-600">
                                                    {collaborator.phone}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-2">

                {/* Other Fields Section */}
                <div className={sectionClass}>
                    <h2 className={sectionHeaderClass}>
                        Other Fields
                    </h2>

                    {/* Status related fields */}
                    <div className="grid grid-cols-3 gap-6 mb-6">
                        <div>
                            <label className={labelClass}>Preferred Stream</label>
                            {renderDropdown(
                                selectedPreferredStream,
                                preferredStreamSearchTerm,
                                "Select Preferred Stream",
                                preferredStreams.map(stream => stream.name),
                                isPreferredStreamDropdownOpen,
                                setSelectedPreferredStream,
                                setPreferredStreamSearchTerm,
                                "preferredStream",
                                preferredStreamDropdownRef
                            )}
                        </div>
                        <div>
                            <label className={labelClass}>State</label>
                            {renderDropdown(
                                selectedState,
                                stateSearchTerm,
                                "Select State",
                                leadStates.map(state => state.state),
                                isStateDropdownOpen,
                                setSelectedState,
                                setStateSearchTerm,
                                "state",
                                stateDropdownRef
                            )}
                        </div>
                        <div>
                            <label className={labelClass}>Lead Source</label>
                            {renderDropdown(
                                selectedLeadSource,
                                leadSourceSearchTerm,
                                "Select Lead Source",
                                leadSources.map(source => source.source),
                                isLeadSourceDropdownOpen,
                                setSelectedLeadSource,
                                setLeadSourceSearchTerm,
                                "leadSource",
                                leadSourceDropdownRef,
                                currentScreen === 'Staff' && isStaff ? true : false
                            )}
                        </div>

                        {selectedLeadSource && leadSubSources.some(subSource => subSource.sourceId === selectedLeadSourceId) && (
                            <div>
                                <label className={labelClass}>Lead Sub Source</label>
                                {renderDropdown(
                                    selectedLeadSubSource,
                                    leadSubSourceSearchTerm,
                                    "Select Lead Sub Source",
                                    leadSubSources.filter(subSource => subSource.sourceId === selectedLeadSourceId).map(subSource => subSource.subSource),
                                    isLeadSubSourceDropdownOpen,
                                    setSelectedLeadSubSource,
                                    setLeadSubSourceSearchTerm,
                                    "leadSubSource",
                                    leadSubSourceDropdownRef,
                                    currentScreen === 'Staff' && isStaff ? true : false
                                )}
                            </div>
                        )}
                        {/* </div> */}

                        {/* Additional status fields */}
                        {/* <div className="grid grid-cols-3 gap-6"> */}
                        <div>
                            <label className={labelClass}>Status</label>
                            {renderDropdown(
                                selectedStatus,
                                statusSearchTerm,
                                "Select Status",
                                leadStatuses.map(status => status.status),
                                isStatusDropdownOpen,
                                setSelectedStatus,
                                setStatusSearchTerm,
                                "status",
                                statusDropdownRef
                            )}
                        </div>
                        {selectedStatus && leadSubStatuses.some(subStatus => subStatus.statusId === selectedStatusId) && (
                            <div>
                                <label className={labelClass}>Sub Status</label>
                                {renderDropdown(
                                    selectedSubStatus,
                                    subStatusSearchTerm,
                                    "Select Sub Status",
                                    leadSubStatuses.filter(subStatus => subStatus.statusId === selectedStatusId).map(subStatus => subStatus.subStatus),
                                    isSubStatusDropdownOpen,
                                    setSelectedSubStatus,
                                    setSubStatusSearchTerm,
                                    "subStatus",
                                    subStatusDropdownRef
                                )}
                            </div>
                        )}
                        <div>
                            <label className={labelClass}>Stage</label>
                            {renderDropdown(
                                selectedStage,
                                stageSearchTerm,
                                "Select Stage",
                                leadStages.map(stage => stage.stage),
                                isStageDropdownOpen,
                                setSelectedStage,
                                setStageSearchTerm,
                                "stage",
                                stageDropdownRef
                            )}
                        </div>
                        {/* Select sub stage if selected stage has sub stages */}
                        {selectedStage && leadSubStages.some(subStage => subStage.stageId === selectedStageId) && (
                            <div>
                                <label className={labelClass}>Sub Stage</label>
                                {renderDropdown(
                                    selectedSubStage,
                                    subStageSearchTerm,
                                    "Select Sub Stage",
                                    leadSubStages.filter(subStage => subStage.stageId === selectedStageId).map(subStage => subStage.subStage),
                                    isSubStageDropdownOpen,
                                    setSelectedSubStage,
                                    setSubStageSearchTerm,
                                    "subStage",
                                    subStageDropdownRef
                                )}
                            </div>
                        )}
                        <div>
                            <label className={labelClass}>Campaign</label>
                            {renderDropdown(
                                selectedCampaign,
                                campaignSearchTerm,
                                "Select Campaign",
                                leadCampaigns.map(campaign => campaign.campaign),
                                isCampaignDropdownOpen,
                                setSelectedCampaign,
                                setCampaignSearchTerm,
                                "campaign",
                                campaignDropdownRef
                            )}
                        </div>
                        <div>
                            <label className={labelClass}>Language</label>
                            {renderDropdown(
                                language,
                                languageSearchTerm,
                                "Select Language",
                                languages.map(lang => lang.language),
                                isLanguageDropdownOpen,
                                setLanguage,
                                setLanguageSearchTerm,
                                "language",
                                languageDropdownRef
                            )}
                        </div>
                    </div>
                </div>

                {/* Admin Notes */}
                {adminNotes && adminNotes.length > 0 && (
                    <div className={sectionClass}>
                        <h2 className={sectionHeaderClass}>Admin Notes</h2>
                        <div className="mb-4">
                            <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded-lg shadow-sm">
                                <p className="text-sm text-gray-700">{adminNotes}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Remarks Section */}
                <div className={sectionClass}>
                    <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
                        <h2 className="text-lg font-semibold text-sky-950">
                            Remarks
                        </h2>
                        {latestRemark && (
                            <button
                                onClick={() => {
                                    const newShowHistory = !showRemarkHistory;
                                    setShowRemarkHistory(newShowHistory);
                                    // Only fetch history when opening and we haven't loaded multiple remarks yet
                                    if (newShowHistory && remarks.length <= 1) {
                                        fetchRemarkHistory();
                                    }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                            >
                                {showRemarkHistory ? 'Hide History' : 'Show History'}
                                <svg
                                    className={`ml-1 w-4 h-4 transition-transform ${showRemarkHistory ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* New remark input - now positioned at the top */}
                    <div className="relative mb-4">
                        <textarea
                            value={newRemark}
                            onChange={(e) => setNewRemark(e.target.value)}
                            placeholder="Add a new remark..."
                            className={`w-full p-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            rows={3}
                            readOnly={!write}
                            disabled={!write}
                        />
                    </div>

                    {/* Always show latest remark */}
                    {latestRemark && (
                        <div className="mb-4">
                            <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded-lg shadow-sm">
                                <p className="text-sm text-gray-700 whitespace-pre-line">{latestRemark.remark}</p>
                                <p className="text-xs text-gray-500 mt-1">{latestRemark.displayName ? `By: ${latestRemark.displayName}` : 'Unknown User'}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                    {latestRemark.createdAt && latestRemark.createdAt.toDate
                                        ? latestRemark.createdAt.toDate().toLocaleString()
                                        : 'Date unknown'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Remarks history */}
                    {showRemarkHistory && (
                        <div className="mb-4">
                            {isLoadingRemarkHistory ? (
                                <div className="p-3 bg-gray-50 rounded-lg text-center">
                                    <div className="animate-pulse flex justify-center my-2">
                                        <div className="h-4 w-4 bg-blue-400 rounded-full mr-1"></div>
                                        <div className="h-4 w-4 bg-blue-400 rounded-full mr-1 animation-delay-200"></div>
                                        <div className="h-4 w-4 bg-blue-400 rounded-full animation-delay-400"></div>
                                    </div>
                                    <p className="text-sm text-gray-500">Loading remark history...</p>
                                </div>
                            ) : remarks.length > 1 ? (
                                <div className="space-y-3">
                                    {/* History divider */}
                                    <div className="flex items-center my-4">
                                        <div className="flex-grow border-t border-gray-300"></div>
                                        <span className="mx-4 text-sm font-medium text-gray-500">History</span>
                                        <div className="flex-grow border-t border-gray-300"></div>
                                    </div>

                                    {/* Only show remarks beyond the first one (older remarks) */}
                                    {remarks.slice(1).map(remark => (
                                        <div key={remark.id} className="p-3 rounded-lg shadow-sm bg-gray-50 border-l-4 border-gray-400 relative">
                                            {/* <button
                                                onClick={() => handleDeleteRemark(remark.id)}
                                                className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                                                title="Delete remark"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button> */}
                                            <p className="text-sm text-gray-700 pr-5 whitespace-pre-line">{remark.remark}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {remark.displayName ? `By: ${remark.displayName}` : 'Unknown User'}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {remark.createdAt && remark.createdAt.toDate
                                                    ? remark.createdAt.toDate().toLocaleString()
                                                    : 'Date unknown'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-3 bg-gray-50 rounded-lg text-center">
                                    <p className="text-sm text-gray-500">No older remarks found</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {write && (
                    <div className="flex justify-center mt-8">
                        <button
                            onClick={confirmSave}
                            disabled={isSaving}
                            className={`
                    px-8 py-3 
                    text-white 
                    bg-gradient-to-r from-[#3483BE] to-[#02385E] 
                    rounded-3xl 
                    font-medium 
                    hover:bg-gray-50 
                    relative
                    ${isSaving ? 'cursor-not-allowed opacity-75' : ''}
                `}
                        >
                            <span className={isSaving ? 'invisible' : ''}>Save</span>
                            {isSaving && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                </div>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <ConfirmationModal isOpen={isConfirmModalOpen} isAdditional={isAdditionalActions} onClose={() => { setIsConfirmModalOpen(false); setAdditionalActions(false); fetchData(); }} onSave={handleSave}
                selectedStudent={{ id: studentId, name: studentName, phone: studentPhone }} />

            {/* Transfer Modal */}

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')}
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};
export default StudentPreferences;